<template>
  <div class="app-root">
    <AppIntro v-if="showIntro && !isKioskRoute" @done="showIntro = false" />
    <div class="global-loader" :class="{ 'is-active': isLoading }" :aria-hidden="(!isLoading).toString()">
      <div class="loader-spinner"></div>
    </div>
    <template v-if="!isKioskRoute">
      <GlobalPopup />
      <CookieConsentBanner />
      <CookiePreferencesModal />
    </template>
    <div v-if="!isKioskRoute && !isMapRoute" class="app-status-fade" aria-hidden="true"></div>
    <div class="app-shell" :class="{ 'app-shell--map': isMapRoute, 'app-shell--auth': isAuthRoute }">
      <DesktopHeader v-if="showDesktopHeader && !isKioskRoute" />
      <router-view />
      <footer v-if="showDesktopFooter && !isKioskRoute" class="desktop-footer">
        <div class="desktop-footer__inner">
          <div class="desktop-footer__links">
            <a class="desktop-footer__link" href="https://www.info.coridor.co/#hakkimizda" target="_blank" rel="noreferrer">{{ $t('footer.about') }}</a>
            <a class="desktop-footer__link" href="https://www.info.coridor.co/#ekibimiz" target="_blank" rel="noreferrer">{{ $t('footer.ourTeam') }}</a>
            <a class="desktop-footer__link" href="https://www.info.coridor.co/#hayalortaklarimiz" target="_blank" rel="noreferrer">{{ $t('footer.ourPartners') }}</a>
            <a class="desktop-footer__link" href="https://www.gucluiyilik.com/" target="_blank" rel="noreferrer">{{ $t('footer.community') }}</a>
            <a class="desktop-footer__link" href="https://www.info.coridor.co/" target="_blank" rel="noreferrer">{{ $t('footer.forOrganizations') }}</a>
            <a class="desktop-footer__link" href="mailto:contact@coridor.co" target="_blank" rel="noreferrer">{{ $t('footer.contact') }}</a>
            <router-link
              v-for="page in footerPages"
              :key="page.slug"
              class="desktop-footer__link"
              :to="{ name: 'static-page', params: { slug: page.slug } }"
            >
              {{ page.title }}
            </router-link>
          </div>
          <span class="desktop-footer__copy">© 2026 Coridor</span>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, onErrorCaptured, onMounted, ref, watch } from 'vue'
import { useAuthStore } from './store/auth'
import { useProfileStore } from './store/profile'
import { useLoaderStore } from './store/loader'
import { useMissionStore } from './store/mission'
import DesktopHeader from './components/DesktopHeader.vue'
import GlobalPopup from './components/GlobalPopup.vue'
import CookieConsentBanner from './components/CookieConsentBanner.vue'
import CookiePreferencesModal from './components/CookiePreferencesModal.vue'
import AppIntro from './components/AppIntro.vue'
import { useRoute } from 'vue-router'
import { initConsent } from './services/consentService'
import pageService from './services/page.service'
import { errorLog } from './services/BaseService'
import { recordAppIntroVisit, shouldShowAppIntro } from './composables/useAppIntro'

const DOMAIN_SPECIAL_MODE_MAP = {
  'kulturyolu.coridor.co': 'kulturyolu26',
}

export default defineComponent({
  name: 'Coridor',
  components: { DesktopHeader, GlobalPopup, CookieConsentBanner, CookiePreferencesModal, AppIntro },
  setup () {
    const authStore = useAuthStore()
    const profileStore = useProfileStore()
    const loaderStore = useLoaderStore()
    const missionStore = useMissionStore()
    const route = useRoute()

    // Decided synchronously (not in onMounted) so it's set before first paint —
    // otherwise the real page could flash briefly before the overlay appears.
    // Kiosk sayfaları (QR ekranı) uygulama kabuğu olmadan açılır. İlk çizimde rota henüz
    // çözülmemiş olabildiği için adres yolundan da bakılır.
    const isKioskPath = typeof window !== 'undefined' && window.location.pathname.startsWith('/qr/')
    const isKioskRoute = computed(() => Boolean(route.meta?.kiosk) || (!route.name && isKioskPath))
    const showIntro = ref(!isKioskPath && shouldShowAppIntro())
    if (!isKioskPath) {
      recordAppIntroVisit()
    }

    // API loading state
    const isLoading = computed(() => loaderStore.isLoading)

    // Prevent Google Maps / Vue VDOM timing errors from clearing the scheduler
    // queue. Without this, a single null-vnode crash during a flush causes all
    // remaining jobs (including router navigation) to be discarded.
    onErrorCaptured((err) => {
      console.error('[Vue ErrorCaptured]', err)
      return false
    })

    onMounted(async () => {
      initConsent()
      if (authStore.isLoggedIn) {
        await profileStore.getProfileList()
      }
    })

    watch(() => authStore.isLoggedIn, (loggedIn) => {
      if (!loggedIn) return
      const autoCode = DOMAIN_SPECIAL_MODE_MAP[window.location.hostname]
      if (!autoCode) return
      // Only auto-apply once per browser session; if the user exits the mode
      // manually, page refreshes should not re-activate it.
      const sessionKey = `autoModeAttempted_${autoCode}`
      if (sessionStorage.getItem(sessionKey)) return
      sessionStorage.setItem(sessionKey, '1')
      if (missionStore.specialModeCode !== autoCode) {
        missionStore.applySpecialModeCode(autoCode).catch(() => {})
      }
    }, { immediate: true })

    const hideHeaderRoutes = new Set([
      'home',
      'login',
      'register',
      'forgot-password',
      'new-password',
      'activate-user',
      'on-google-auth',
      'two-factor-verify'
    ])

    const showDesktopHeader = computed(() => !hideHeaderRoutes.has(route.name))
    const isMapRoute = computed(() => route.name === 'home-map')
    const authRoutes = new Set([
      'login',
      'register',
      'forgot-password',
      'new-password',
      'two-factor-verify'
    ])
    const isAuthRoute = computed(() => authRoutes.has(route.name))
    const showDesktopFooter = computed(() => {
      if (isMapRoute.value || route.name === 'home') return false
      return !authRoutes.has(route.name)
    })

    const footerPages = ref([])

    onMounted(async () => {
      try {
        const response = await pageService.getFooterPages()
        footerPages.value = response.data?.data || []
      } catch (err) {
        errorLog('Failed to load footer pages', err)
      }
    })

    return {
      authStore,
      profileStore,
      showDesktopHeader,
      isMapRoute,
      isAuthRoute,
      showDesktopFooter,
      footerPages,
      isLoading,
      showIntro,
      isKioskRoute
    }
  }
})
</script>

<style scoped lang="scss">
#app {
  font-family: 'Poppins', sans-serif !important;
}
*{
   font-family: 'Poppins', sans-serif !important;
}
/* Hide scrollbar in Chrome, Safari and Opera */
 #app::-webkit-scrollbar {
   display:none;
 }

 /* Hide scrollbar for IE, Edge and Firefox */
 #app {
 -ms-overflow-style: none; /* IE and EDGE */;
  scrollbar-width: none; /* Firefox */
}

</style>

<style>
/* Uygulamada kaydırılan içerik durum çubuğu/dinamik ada altında okunmasın diye beyazdan şeffafa geçiş.
   Yüksekliği güvenli alandan türediği için tarayıcıda 0. */
.app-status-fade {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  /* Başlık güvenli alanın hemen altından başladığı için geçiş onun üstünde bitiyor; harita sayfasında hiç yok. */
  height: calc(var(--app-safe-top) * 1.15);
  /* Doğrusal geçiş keskin bir çizgi gibi görünüyordu; yumuşak (ease-out) bir eğri için çok duraklı. */
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 1) 45%,
    rgba(255, 255, 255, 0.9) 57%,
    rgba(255, 255, 255, 0.72) 68%,
    rgba(255, 255, 255, 0.48) 78%,
    rgba(255, 255, 255, 0.24) 88%,
    rgba(255, 255, 255, 0.08) 95%,
    rgba(255, 255, 255, 0) 100%
  );
  pointer-events: none;
  z-index: 1035;
}

/* viewport-fit=cover ile içerik durum çubuğunun altına uzanıyor; harita kendi boşluğunu yönetiyor. */
.app-shell:not(.app-shell--map) {
  padding-top: var(--app-safe-top);
}

.global-loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.18s ease, visibility 0.18s ease;
}

.global-loader.is-active {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

.loader-spinner {
  width: 48px;
  height: 48px;
  border: 5px solid #f3f3f3;
  border-bottom-color: var(--brand-color);
  border-radius: 50%;
  display: inline-block;
  box-sizing: border-box;
  animation: rotation 1s linear infinite;
}
@keyframes rotation {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
